import { Text, View } from "@react-pdf/renderer";
import { styles } from "../../ReactPdfStyles";
import { formatTableDataLabel } from "../utils";

type LessonDetailSectionTypes = {
	discipline: string;
	lesson_learned_type: string;
	origin: string;
	lesson_learned_classification: string;
	section: string;
	what_planned: string;
	what_happened: string;
	what_learned: string;
	what_actions_required: string;
	what_was_done: string;
};

export function LessonDetailSection({
	discipline,
	lesson_learned_type,
	origin,
	lesson_learned_classification,
	section,
	what_planned,
	what_happened,
	what_learned,
	what_actions_required,
	what_was_done,
}: LessonDetailSectionTypes) {
	const lessonData = {
		discipline: discipline,
		lessonType: lesson_learned_type,
		origin: origin,
		lessonClassification: lesson_learned_classification,
		section: section,
		whatWasPlanned: what_planned,
		whatHappened: what_happened,
		whatWasLearned: what_learned,
		whatActionsAreRequired: what_actions_required,
		whatWasDone: what_was_done,
	};

	// fields that should take full row
	const fullRowFields = new Set([
		"whatWasPlanned",
		"whatHappened",
		"whatWasLearned",
		"whatActionsAreRequired",
		"whatWasDone",
	]);

	// turn object into label/value pairs, but skip empty/undefined ones
	const flatEntries = Object.entries(lessonData)
		.filter(
			([_, value]) =>
				value !== undefined && value !== null && String(value).trim() !== ""
		)
		.map(([key, value]) => [key, formatTableDataLabel(key), String(value)]);

	// group into rows
	const rows: { label: string; value: string; fullRow: boolean }[] = [];
	for (let i = 0; i < flatEntries.length; i++) {
		const [rawKey, label, value] = flatEntries[i];
		rows.push({ label, value, fullRow: fullRowFields.has(rawKey) });
	}

	return (
		<View style={[styles.section, styles.tableFontSize]}>
			<Text style={styles.tableHeader}>Lesson detail</Text>
			<View style={styles.row}>
				<View style={{ width: "100%", flexDirection: "column" }}>
					{rows.map((row, idx) =>
						row.fullRow ? (
							<View
								style={[
									styles.row,
									...(row.label === "What was learned"
										? [{ backgroundColor: "#CDCDCD" }]
										: []),
								]}
								key={idx}
								// wrap={false} // Either fit content on the current page (if space is available) or push the whole thing to the next page (if not enough spaces)
							>
								<Text style={styles.labelLongCell}>{row.label}</Text>
								<Text style={[styles.valueLongCell, styles.borderRight]}>
									{row.value === "N/A" ? (
										<Text style={styles.naText}>{row.value}</Text>
									) : (
										row.value
									)}
								</Text>
							</View>
						) : (
							idx % 2 === 0 &&
							(!rows[idx + 1] || rows[idx + 1].fullRow ? (
								// Row with leftover cells
								<View style={styles.row} key={idx}>
									<Text style={styles.labelCell}>{row.label}</Text>
									<Text style={[styles.valueCell]}>
										{row.value === "N/A" ? (
											<Text style={styles.naText}>{row.value}</Text>
										) : (
											row.value
										)}
									</Text>
									<Text style={styles.mergeCell}></Text>
									<Text style={[styles.mergeCell, styles.borderRight]}></Text>
								</View>
							) : (
								// Normal row
								<View style={styles.row} key={idx}>
									<Text style={styles.labelCell}>{row.label}</Text>
									<Text style={[styles.valueCell]}>
										{row.value === "N/A" ? (
											<Text style={styles.naText}>{row.value}</Text>
										) : (
											row.value
										)}
									</Text>
									<Text style={styles.labelCell}>{rows[idx + 1].label}</Text>
									<Text style={[styles.valueCell, styles.borderRight]}>
										{rows[idx + 1].value === "N/A" ? (
											<Text style={styles.naText}>{rows[idx + 1].value}</Text>
										) : (
											rows[idx + 1].value
										)}
									</Text>
								</View>
							))
						)
					)}
				</View>
			</View>
		</View>
	);
}

export default LessonDetailSection;
